import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './TopPosts.css';

interface TopPostItem {
  id: string;
  userId: string;
  userName: string;
  department: string;
  type: 'before-after' | 'video';
  timestamp: string;
  location?: string;
  points?: number;
  reactions: {
    love: string[];
    clap: string[];
    fire: string[];
    muscle: string[];
  };
  totalReactions: number;
  comments: Array<{
    id: string;
    userId: string;
    userName: string;
    content: string;
    timestamp: string;
  }>;
}

interface TopPostsProps {
  onNavigate: (page: string) => void;
}

const TopPosts: React.FC<TopPostsProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<TopPostItem[]>([]);
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'month' | 'all'>('week');

  useEffect(() => {
    // Load activities from localStorage and calculate total reactions
    const savedActivities = JSON.parse(localStorage.getItem('campusActivities') || '[]');
    
    // Sample top activities
    const sampleTopPosts: TopPostItem[] = [
      {
        id: 'top-1',
        userId: 'user-star-1',
        userName: 'Maria Rodriguez',
        department: 'Environmental Science',
        type: 'before-after',
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
        location: 'Main Cafeteria',
        points: 150,
        reactions: {
          love: Array(45).fill(0).map((_, i) => `user-${i}`),
          clap: Array(32).fill(0).map((_, i) => `user-${i + 45}`),
          fire: Array(28).fill(0).map((_, i) => `user-${i + 77}`),
          muscle: Array(15).fill(0).map((_, i) => `user-${i + 105}`)
        },
        totalReactions: 120,
        comments: [
          {
            id: 'tc-1',
            userId: 'user-2',
            userName: 'John Doe',
            content: 'This is incredible! The transformation is amazing! 🌟',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
          },
          {
            id: 'tc-2',
            userId: 'user-3',
            userName: 'Sarah Lee',
            content: 'Wow! This should be the standard for all areas!',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
          }
        ]
      },
      {
        id: 'top-2',
        userId: 'user-star-2',
        userName: 'Ahmed Hassan',
        department: 'Engineering',
        type: 'video',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        location: 'Library Garden',
        points: 100,
        reactions: {
          love: Array(38).fill(0).map((_, i) => `user-${i}`),
          clap: Array(25).fill(0).map((_, i) => `user-${i + 38}`),
          fire: Array(20).fill(0).map((_, i) => `user-${i + 63}`),
          muscle: Array(12).fill(0).map((_, i) => `user-${i + 83}`)
        },
        totalReactions: 95,
        comments: []
      },
      {
        id: 'top-3',
        userId: 'user-star-3',
        userName: 'Priya Sharma',
        department: 'Biology',
        type: 'before-after',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        location: 'Science Lab Entrance',
        points: 125,
        reactions: {
          love: Array(30).fill(0).map((_, i) => `user-${i}`),
          clap: Array(22).fill(0).map((_, i) => `user-${i + 30}`),
          fire: Array(18).fill(0).map((_, i) => `user-${i + 52}`),
          muscle: Array(10).fill(0).map((_, i) => `user-${i + 70}`)
        },
        totalReactions: 80,
        comments: []
      }
    ];

    // Combine and calculate total reactions
    const allPosts = [...savedActivities, ...sampleTopPosts].map(post => ({
      ...post,
      totalReactions: (post.reactions?.love?.length || 0) + 
                     (post.reactions?.clap?.length || 0) + 
                     (post.reactions?.fire?.length || 0) + 
                     (post.reactions?.muscle?.length || 0)
    }));

    // Filter by time
    const now = new Date();
    const filteredPosts = allPosts.filter(post => {
      const postDate = new Date(post.timestamp);
      const daysDiff = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (timeFilter) {
        case 'today':
          return daysDiff < 1;
        case 'week':
          return daysDiff < 7;
        case 'month':
          return daysDiff < 30;
        default:
          return true;
      }
    });

    // Sort by total reactions and take top 10
    const topPosts = filteredPosts
      .sort((a, b) => b.totalReactions - a.totalReactions)
      .slice(0, 10);

    setPosts(topPosts);
  }, [timeFilter]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    }
  };

  const getRankEmoji = (index: number) => {
    switch (index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return `#${index + 1}`;
    }
  };

  return (
    <div className="top-posts-page">
      <div className="top-posts-header">
        <button onClick={() => onNavigate('dashboard')} className="back-btn">
          ← Back
        </button>
        <h2>🔥 Trending Posts</h2>
        <p>Most popular cleaning activities</p>
      </div>

      <div className="time-filters">
        <button 
          className={`filter-btn ${timeFilter === 'today' ? 'active' : ''}`}
          onClick={() => setTimeFilter('today')}
        >
          Today
        </button>
        <button 
          className={`filter-btn ${timeFilter === 'week' ? 'active' : ''}`}
          onClick={() => setTimeFilter('week')}
        >
          This Week
        </button>
        <button 
          className={`filter-btn ${timeFilter === 'month' ? 'active' : ''}`}
          onClick={() => setTimeFilter('month')}
        >
          This Month
        </button>
        <button 
          className={`filter-btn ${timeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setTimeFilter('all')}
        >
          All Time
        </button>
      </div>

      <div className="top-posts-content">
        {posts.length === 0 ? (
          <div className="empty-state">
            <p>No trending posts for this time period</p>
          </div>
        ) : (
          <div className="posts-list">
            {posts.map((post, index) => (
              <div key={post.id} className={`top-post-card ${index < 3 ? 'podium' : ''}`}>
                <div className="rank-badge">{getRankEmoji(index)}</div>
                
                <div className="post-header">
                  <div className="user-info">
                    <h4>{post.userName}</h4>
                    <p>{post.department} • {formatTimestamp(post.timestamp)}</p>
                  </div>
                  <div className="stats">
                    <div className="reaction-count">
                      <span className="count">{post.totalReactions}</span>
                      <span className="label">reactions</span>
                    </div>
                    {post.points && (
                      <div className="points-earned">
                        +{post.points} pts
                      </div>
                    )}
                  </div>
                </div>

                <div className="post-content">
                  <p>
                    {post.type === 'before-after' 
                      ? `📸 Before/After at ${post.location}` 
                      : `🎬 Cleaning video from ${post.location}`}
                  </p>
                  
                  {post.type === 'before-after' && (
                    <div className="media-preview">
                      <div className="before-after-mini">
                        <div className="mini-image before">Before</div>
                        <div className="mini-image after">After</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="reactions-breakdown">
                  <div className="reaction-item">
                    <span>❤️</span>
                    <span>{post.reactions.love.length}</span>
                  </div>
                  <div className="reaction-item">
                    <span>👏</span>
                    <span>{post.reactions.clap.length}</span>
                  </div>
                  <div className="reaction-item">
                    <span>🔥</span>
                    <span>{post.reactions.fire.length}</span>
                  </div>
                  <div className="reaction-item">
                    <span>💪</span>
                    <span>{post.reactions.muscle.length}</span>
                  </div>
                </div>

                {post.comments.length > 0 && (
                  <div className="top-comments">
                    <p className="comments-header">💬 Top Comments</p>
                    {post.comments.slice(0, 2).map(comment => (
                      <div key={comment.id} className="comment-preview">
                        <strong>{comment.userName}:</strong> {comment.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopPosts;